// src/contexts/AppContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const AppContext = createContext({
  themeMode: 'light',
  toggleTheme: () => {},
  notifications: [],
  addNotification: () => {},
  removeNotification: () => {},
});

export const AppProvider = ({ children, themeMode: initialMode = 'light', toggleTheme: externalToggle }) => {
  const [themeMode, setThemeMode] = useState(initialMode);
  const [notifications, setNotifications] = useState([]);

  const toggleTheme = () => {
    if (externalToggle) {
      externalToggle();
      return;
    }
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    localStorage.setItem('theme-mode', newMode);
  };

  // Sync with external theme mode changes
  React.useEffect(() => {
    setThemeMode(initialMode);
  }, [initialMode]);

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const addNotification = ({ id, type, message, duration = 5000, action }) => {
    const key = id || Date.now().toString();
    setNotifications((prev) => [...prev, { id: key, type, message, action }]);
    if (duration > 0) {
      setTimeout(() => removeNotification(key), duration);
    }
  };

  return (
    <AppContext.Provider value={{ themeMode, toggleTheme, notifications, addNotification, removeNotification }}>
      {children}
    </AppContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired,
  themeMode: PropTypes.oneOf(['light', 'dark']),
  toggleTheme: PropTypes.func,
};

// Export hook with expected name useApp
export const useApp = () => useContext(AppContext);
